/**
 * Unified visibility configuration for book views
 * 
 * Uses DB field names for consistency across all views.
 * Single configuration works for both Full Cards and Extended List views.
 */

import type { FieldVisibility, FieldConfig } from '@/types'

/**
 * Default visibility for all view modes
 * Users can override these settings via UI, persisted in localStorage
 */
export const DEFAULT_VISIBILITY: FieldVisibility = {
    // Core fields (always visible)
    title: true, // Title - NON-TOGGLEABLE
    author: true, // Author(s)

    // Common fields (visible by default)
    category: true, // Categories
    series: true, // Series name
    progress: true, // Reading progress
    status: true, // Book status

    // Extended fields (hidden by default to reduce clutter)
    language: false, // Language
    publisher: false, // Publisher
    series_number: false, // Series number
    format: false, // Format (Physical/Digital)
    location: false, // Location
    page_count: false, // Page count
    notes: false, // User notes
    synopsis: false, // Synopsis
    description: false, // Description
    created_at: false, // Date added
}

/**
 * Field metadata for UI rendering
 * Maps DB field keys to user-friendly Spanish labels
 */
export const FIELD_LABELS: Record<string, string> = {
    title: 'Título',
    author: 'Autor',
    category: 'Categoría',
    series: 'Serie',
    series_number: '#',
    progress: 'Progreso',
    status: 'Estado',
    language: 'Idioma',
    publisher: 'Editorial',
    format: 'Formato',
    location: 'Ubicación',
    page_count: 'Páginas',
    notes: 'Notas',
    synopsis: 'Sinopsis',
    description: 'Descripción',
    created_at: 'Fecha de Adición',
}

/**
 * Fields that cannot be toggled off (always visible)
 */
export const NON_TOGGLEABLE_FIELDS = ['title'] as const

/**
 * Get field configuration array for UI rendering
 * @param visibility - Current visibility state
 * @returns Array of {key, label, checked, disabled}
 */
export function getFieldConfigArray(visibility: FieldVisibility): FieldConfig[] {
    return Object.keys(FIELD_LABELS).map((key) => {
        const label = FIELD_LABELS[key]
        const savedValue = Object.hasOwn(visibility, key) ? visibility[key] : undefined
        const defaultValue = Object.hasOwn(DEFAULT_VISIBILITY, key) ? DEFAULT_VISIBILITY[key] : false
        const isChecked = savedValue !== undefined ? savedValue : defaultValue

        return {
            key: key as keyof FieldVisibility,
            label,
            checked: isChecked ?? false,
            disabled: NON_TOGGLEABLE_FIELDS.includes(key as (typeof NON_TOGGLEABLE_FIELDS)[number]),
        }
    })
}
